import type { ReactNode } from 'react';
import { Box } from '@chakra-ui/react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import type { SwipeDirection } from '../ratingCategories';

const MotionBox = motion.create(Box);

interface RatingStepProps {
  /** Unique key for this step (used for AnimatePresence) */
  stepKey: string;
  /** The content to render inside the step */
  children: ReactNode;
  /** Direction of the current swipe animation */
  swipeDirection: SwipeDirection;
  /** Swiped left — go to next category */
  onSwipeLeft?: () => void;
  /** Swiped right — go to previous category */
  onSwipeRight?: () => void;
}

/**
 * Animated + swipeable wrapper for a single category card. Scoped to the
 * card region only — the Back/Next footer and progress dots live outside
 * this component and are never draggable (broad touch interception on the
 * whole modal previously broke WKWebView scroll/taps, see
 * RestaurantRatingModal.tsx's onClick comment).
 *
 * `mode="popLayout"` (not "wait" or "sync"): entering and exiting cards
 * animate SIMULTANEOUSLY, since the 400ms auto-advance timer already
 * provides the pacing — stacking a "wait" exit in front of it would feel
 * sluggish. Plain "sync" overlaps timing but not layout — both cards stay in
 * normal flow for the duration of the crossfade, doubling the container's
 * height and dropping the entering card below the exiting one. "popLayout"
 * absolutely-positions the exiting element during its exit animation so the
 * entering card lays out as if it were the only child. AnimatePresence
 * still unmounts the exiting card once its exit completes.
 */
export function RatingStep({
  stepKey,
  children,
  swipeDirection,
  onSwipeLeft,
  onSwipeRight,
}: RatingStepProps) {
  const prefersReducedMotion = useReducedMotion();

  const handleDragEnd = (
    _: unknown,
    info: { offset: { x: number }; velocity: { x: number } }
  ) => {
    const swipeThreshold = 50;
    const velocityThreshold = 500;

    if (info.offset.x < -swipeThreshold || info.velocity.x < -velocityThreshold) {
      onSwipeLeft?.();
    } else if (info.offset.x > swipeThreshold || info.velocity.x > velocityThreshold) {
      onSwipeRight?.();
    }
  };

  return (
    // Clips the X axis ONLY — what the swipe transition needs (entering and
    // exiting cards slide in x, never y) — while leaving the Y axis truly
    // unclipped so the selected emoji's glow can bleed out of this box.
    //
    // `clip-path` rather than `overflow-x`. This is NOT a style preference;
    // `overflow-x: hidden` cannot express "clip X, not Y". Per CSS Overflow
    // Level 3, when one axis computes to a non-`visible` value, the other
    // axis's `visible` computes to `auto`. So `overflow-x: hidden` alone
    // silently yields a computed `overflow-y: auto` — verified live: the
    // authored rule was `{ width:100%; overflow-x:hidden; position:relative }`
    // with no overflow-y at all, yet getComputedStyle reported
    // `overflow-x: hidden / overflow-y: auto`.
    //
    // Two defects followed from that coerced `auto`, both reported as "a hard
    // line right beneath the emoji row that cuts into the glow":
    //   1. It clips vertically with zero bottom padding. The ring's bottom sat
    //      8.45px above this box's edge while the selected `drop-shadow(0 0
    //      40px)` needs ~40px, so the halo was sliced off mid-gradient.
    //      (RestaurantRatingModal's `pb={10}` does not help — that padding is
    //      on the OUTER scroll container, ~445px further down.)
    //   2. `auto` also makes this a scroll container, which gets its own
    //      compositing layer. The modal overlay's `backdrop-filter: blur()`
    //      then renders differently inside vs. outside that layer, painting
    //      this box as a visible rectangle with hard bottom/right edges —
    //      present across the row's full width, not just under the glow, and
    //      made MORE visible by raising the blur radius.
    // Measured on the live page (luminance step across one pixel row, local
    // noise floor ~0.07): 1.498 at the bottom edge full-width and 10.106 in
    // the selected emoji's column; both fall to noise (0.116 / 0.023) once
    // the vertical clip is gone.
    //
    // Negative Y insets expand the clip region past the box; `0px` on X keeps
    // the horizontal clip exactly where `overflow-x: hidden` had it (verified
    // by hit-testing a translated child against a no-clip control). Unlike
    // `overflow-x: clip` — the other spec-correct way to clip one axis, which
    // needs Safari/iOS 16 — `clip-path` is supported from Safari/iOS 14, this
    // project's `.browserslistrc` floor.
    <Box
      w="100%"
      position="relative"
      sx={{
        clipPath: 'inset(-100vh 0px)',
        WebkitClipPath: 'inset(-100vh 0px)',
      }}
    >
      <AnimatePresence mode="popLayout">
        <MotionBox
          key={stepKey}
          initial={
            prefersReducedMotion
              ? { opacity: 0 }
              : { opacity: 0, x: swipeDirection === 'right' ? 100 : -100 }
          }
          animate={{ opacity: 1, x: 0 }}
          exit={
            prefersReducedMotion
              ? { opacity: 0 }
              : { opacity: 0, x: swipeDirection === 'right' ? -100 : 100 }
          }
          transition={{ duration: prefersReducedMotion ? 0.15 : 0.3, ease: 'easeOut' }}
          // drag stays enabled under reduced motion — only the follow-the-finger
          // visual is suppressed (dragElastic 0 pins the card at the {0,0}
          // constraint during drag). onDragEnd's offset/velocity threshold still
          // fires regardless, so swiping remains the only forward-nav path on
          // mobile widths (no Back/Next footer below `md`).
          drag="x"
          dragDirectionLock
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={prefersReducedMotion ? 0 : 0.2}
          onDragEnd={handleDragEnd}
          cursor={prefersReducedMotion ? 'default' : 'grab'}
          _active={{ cursor: prefersReducedMotion ? 'default' : 'grabbing' }}
          w="100%"
        >
          {children}
        </MotionBox>
      </AnimatePresence>
    </Box>
  );
}

export default RatingStep;
